import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { API } from '../config';
import IndagaAnim from './IndagaAnim';
import IndagaConfrontaDialog from './IndagaConfrontaDialog';
import { useBgReport, useBgJob } from './BackgroundJobs';

// Dialog "Indaga" (reverse lookup di un numero su Google Maps). Estetica dei dialog agenti
// (.bulk-overlay/.bulk-dialog, vetro fumé). Riconosce se il numero è già in archivio, permette
// il confronto/aggiornamento (2ª finestra) o la creazione. L'indagine (numero e/o dominio) può
// restare in background: chiudendo la finestra compare un pulsante fluttuante che la riapre.
const IconClose = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
);

const STEP_LABEL = { n456: '456', copertura: 'Copertura', lucchetto: 'Lucchetto' };
const STEPS = ['n456', 'copertura', 'lucchetto'];
const stepFinito = (s) => !!(s && s.stato && s.stato !== 'in_corso' && s.stato !== 'attesa');
const stepEsito = (s) => {
  if (!s) return '—';
  if (s.stato === 'in_corso' || s.stato === 'attesa') return '…';
  if (s.stato === 'saltato') return 'saltato';
  if (s.stato === 'errore') return 'errore';
  return s.esito || 'ok';
};
const chipStyle = (s) => {
  let bg = 'rgba(255,255,255,.1)', col = 'rgba(255,255,255,.7)';
  if (s && s.stato === 'errore') { bg = 'rgba(239,68,68,.22)'; col = '#fca5a5'; }
  else if (s && s.stato === 'saltato') { bg = 'rgba(255,255,255,.08)'; col = 'rgba(255,255,255,.5)'; }
  else if (stepFinito(s)) { bg = 'rgba(34,197,94,.22)'; col = '#86efac'; }
  return { fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: bg, color: col, whiteSpace: 'nowrap' };
};

// dominio pulito dall'URL del sito (toglie schema/www/percorso)
const estraiDominio = (sito) => {
  if (!sito) return '';
  try {
    const u = new URL(/^https?:\/\//i.test(sito) ? sito : 'https://' + sito);
    return (u.hostname || '').replace(/^www\./i, '');
  } catch { return ''; }
};

function Riga({ l, children }) {
  if (!children) return null;
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 13, padding: '4.5px 0', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
      <span style={{ color: 'rgba(255,255,255,.55)', flex: '0 0 84px' }}>{l}</span>
      <span style={{ color: '#fff', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
    </div>
  );
}

export default function IndagaDialog({ open, onClose, canDominio }) {
  const [numero, setNumero] = useState('');
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [domJob, setDomJob] = useState(null);
  const [domBusy, setDomBusy] = useState(false);
  const [domDominio, setDomDominio] = useState('');
  const [confrontaOpen, setConfrontaOpen] = useState(false);
  const [creaBusy, setCreaBusy] = useState(false);
  const [creato, setCreato] = useState(false);
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);
  // alla chiusura NON interrompo l'indagine: chiudo solo la sotto-finestra di confronto
  useEffect(() => { if (!open) setConfrontaOpen(false); }, [open]);

  const clearSessione = () => {
    clearInterval(poll.current);
    setNumero(''); setBusy(false); setData(null);
    setDomJob(null); setDomBusy(false); setDomDominio('');
    setCreato(false); setConfrontaOpen(false);
  };

  const avvia = async () => {
    const n = numero.trim();
    if (!n) return;
    clearInterval(poll.current); setDomJob(null); setDomBusy(false); setDomDominio(''); setConfrontaOpen(false); setCreato(false);
    setBusy(true); setData(null);
    try { const r = await axios.post(`${API}/indaga`, { numero: n, match_archivio: true }); setData(r.data); }
    catch (e) { setData({ trovato: false, errore: e.response?.data?.detail || 'Errore Indaga' }); }
    setBusy(false);
  };

  const creaRecord = async () => {
    if (!data) return;
    setCreaBusy(true);
    try {
      await axios.post(`${API}/indaga/crea-record`, {
        numero, numero_verificato: data.numero_verificato || null,
        ragione_sociale: data.ragione_sociale, indirizzo: data.indirizzo, comune: data.comune,
        partita_iva: data.partita_iva, email: data.email, social: data.social,
        maps_categoria: data.categoria, maps_url: data.maps_url, force: true,
      });
      setCreato(true); // dopo la creazione niente più Confronta (stesso record, nessuna differenza)
    } catch { /* silenzioso */ }
    setCreaBusy(false);
  };

  const dominio = estraiDominio(data?.sito);

  const indagaDominio = async () => {
    if (!dominio) return;
    clearInterval(poll.current);
    setDomBusy(true); setDomJob(null); setDomDominio(dominio);
    try {
      const r = await axios.post(`${API}/dominio/verifica`, { dominio });
      const id = r.data.job_id;
      poll.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/dominio/verifica/${id}`);
          setDomJob(s.data);
          if (s.data.stato === 'completato' || s.data.stato === 'errore') { clearInterval(poll.current); setDomBusy(false); }
        } catch { /* ritenta al prossimo tick */ }
      }, 1500);
    } catch (e) {
      setDomBusy(false);
      setDomJob({ stato: 'errore', errore: e.response?.data?.detail || 'Errore avvio dominio' });
    }
  };

  const dd = domJob?.dati || {};
  const step = domJob?.step || {};
  const social = data?.social && typeof data.social === 'object' && Object.keys(data.social).length ? data.social : null;

  // avanzamento indagine dominio (0..100) dai passi completati
  const fatti = STEPS.filter((k) => stepFinito(step[k])).length;
  const pct = domJob?.stato === 'completato' ? 100 : Math.max(8, Math.round((fatti / STEPS.length) * 100));
  const running = busy || domBusy;
  const domDone = !!domJob && (domJob.stato === 'completato' || domJob.stato === 'errore');
  const active = busy || domBusy || !!data || !!domJob;
  const summary = data ? (data.errore ? 'errore' : (data.trovato ? (data.ragione_sociale || 'trovato') : 'nessun risultato')) : null;
  // riporta lo stato alla pila di FAB condivisa (background + notifica in basso a destra)
  useBgReport('indaga', { label: 'Indaga', color: '#f97316', iconKey: 'indaga' },
    { open, running, done: !running && (!!data || domDone), error: !!(data && data.errore), summary, active });

  // reset locale quando la notifica viene chiusa (×)
  const bgEntry = useBgJob('indaga');
  const hadE = useRef(false);
  useEffect(() => { if (bgEntry) hadE.current = true; else if (hadE.current) { hadE.current = false; clearSessione(); } }, [bgEntry]); // eslint-disable-line

  if (!open) return null;

  // ── Vista APERTA ──
  return createPortal(
    <>
      <div className="bulk-overlay" onClick={onClose}>
        <div className="bulk-dialog indaga-dlg" onClick={(e) => e.stopPropagation()}>
          <div className="bulk-content">
            <div className="bulk-head" style={{ marginBottom: 14 }}>
              <span className="bulk-title">Indaga · reverse lookup</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {(busy || domBusy) && (
                  <button onClick={onClose} title="Metti in background: l'indagine continua, riapri dal pulsante fluttuante in basso a destra"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,.22)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    ⤓ Background
                  </button>
                )}
                <button className="bulk-x" onClick={onClose}><IconClose /></button>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="Numero da indagare (es. 0461 123456)"
                onKeyDown={(e) => { if (e.key === 'Enter') avvia(); }} disabled={busy}
                style={{ flex: 1, minWidth: 0, padding: '8px 12px', borderRadius: 9, border: '1px solid rgba(255,255,255,.14)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 13 }} />
              <button onClick={avvia} disabled={busy || !numero.trim()}
                style={{ padding: '8px 15px', borderRadius: 9, border: 0, fontWeight: 800, fontSize: 13, cursor: busy || !numero.trim() ? 'default' : 'pointer', opacity: busy || !numero.trim() ? 0.6 : 1, color: '#fff', background: 'linear-gradient(180deg,#fb923c,#ea580c)' }}>
                {busy ? '…' : 'Indaga'}
              </button>
            </div>

            {busy ? (
              <div style={{ display: 'grid', placeItems: 'center', padding: '18px 0' }}><IndagaAnim /></div>
            ) : data ? (
              data.errore ? <div style={{ color: '#fca5a5', fontSize: 13, padding: '8px 0' }}>{data.errore}</div>
                : !data.trovato ? <div style={{ color: 'rgba(255,255,255,.6)', fontSize: 13, padding: '8px 0' }}>Nessun risultato su Google Maps per questo numero.</div>
                  : (
                    <div>
                      {/* Intestazione compatta: stato (pillola piccola) + nome + azioni, tutto su UNA riga */}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                        {(creato || data.esiste === true || data.esiste === false) && (
                          <span title={creato ? 'Creato in archivio' : data.esiste ? `Già in archivio${data.record?.ragione_sociale ? ': ' + data.record.ragione_sociale : ''}` : 'Non presente in archivio'}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600, flex: '0 0 auto', whiteSpace: 'nowrap', color: 'rgba(255,255,255,.6)' }}>
                            <span style={{ width: 7, height: 7, borderRadius: 999, flex: '0 0 auto', background: creato || data.esiste === true ? '#22c55e' : 'rgba(255,255,255,.4)' }} />
                            {creato ? 'creato' : data.esiste === true ? 'in archivio' : 'nuovo'}
                          </span>
                        )}
                        <div style={{ fontWeight: 800, fontSize: 14, color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.ragione_sociale || ''}>{data.ragione_sociale || '—'}</div>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'nowrap', flex: '0 0 auto' }}>
                          {dominio && canDominio && (
                            <button onClick={indagaDominio} disabled={domBusy} title="Indaga dominio: 456 + Copertura + Lucchetto"
                              style={{ padding: '5px 9px', borderRadius: 7, border: '1px solid rgba(249,115,22,.5)', fontWeight: 800, fontSize: 11.5, cursor: domBusy ? 'default' : 'pointer', color: '#fdba74', background: 'rgba(249,115,22,.12)', whiteSpace: 'nowrap' }}>
                              {domBusy ? '…' : '⌕ Dominio'}
                            </button>
                          )}
                          {data.esiste === true && !creato && (
                            <button onClick={() => setConfrontaOpen(true)} title="Confronta con l'archivio e scegli i campi da aggiornare"
                              style={{ padding: '5px 9px', borderRadius: 7, border: '1px solid rgba(255,255,255,.22)', fontWeight: 800, fontSize: 11.5, cursor: 'pointer', color: '#fff', background: 'rgba(255,255,255,.06)', whiteSpace: 'nowrap' }}>
                              ⇄ Confronta{data.proposte && data.proposte.length ? ` ${data.proposte.length}` : ''}
                            </button>
                          )}
                          {data.esiste === false && !creato && (
                            <button onClick={creaRecord} disabled={creaBusy} title="Crea il record in archivio con i dati trovati"
                              style={{ padding: '5px 10px', borderRadius: 7, border: 0, fontWeight: 800, fontSize: 11.5, cursor: creaBusy ? 'default' : 'pointer', color: '#fff', background: 'linear-gradient(180deg,#fb923c,#ea580c)', whiteSpace: 'nowrap' }}>
                              {creaBusy ? '…' : '＋ Crea'}
                            </button>
                          )}
                        </div>
                      </div>

                      <Riga l="P.IVA">{data.partita_iva}</Riga>
                      <Riga l="Telefono">{data.numero_verificato || data.telefono_maps || numero}</Riga>
                      <Riga l="Indirizzo">{[data.indirizzo, data.comune, data.provincia && `(${data.provincia})`].filter(Boolean).join(' ')}</Riga>
                      <Riga l="Categoria">{data.categoria}</Riga>
                      <Riga l="Email">{data.email}</Riga>
                      <Riga l="Sito">{dominio ? <a href={data.sito} target="_blank" rel="noreferrer" style={{ color: '#fdba74' }}>{dominio}</a> : null}</Riga>
                      <Riga l="Social">{social ? (
                        <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          {Object.entries(social).map(([nome, url]) => (
                            <a key={nome} href={url} target="_blank" rel="noreferrer" style={{ color: '#fdba74', textTransform: 'capitalize' }}>{nome}</a>
                          ))}
                        </span>
                      ) : null}</Riga>

                      {/* Indagine dominio: una sola barra di avanzamento; a fine, esiti compatti */}
                      {(domBusy || domJob) && (
                        <div style={{ marginTop: 12, padding: 10, borderRadius: 11, border: '1px solid rgba(255,255,255,.12)', background: 'rgba(0,0,0,.22)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                            <span style={{ fontWeight: 800, fontSize: 13, color: '#fdba74', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Indagine dominio · {domDominio || dominio}</span>
                            {domBusy && <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,.55)', flex: '0 0 auto' }}>analisi in corso…</span>}
                          </div>
                          {domJob?.errore ? (
                            <div style={{ color: '#fca5a5', fontSize: 13 }}>{domJob.errore}</div>
                          ) : domBusy ? (
                            <div className="indaga-track"><div className="indaga-fill" style={{ width: `${pct}%` }} /></div>
                          ) : (
                            <>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {STEPS.map((k) => (<span key={k} style={chipStyle(step[k])}>{STEP_LABEL[k]}: {stepEsito(step[k])}</span>))}
                              </div>
                              {(dd.partita_iva || dd.telefono || dd.indirizzo) && (
                                <div style={{ marginTop: 8, fontSize: 12, color: 'rgba(255,255,255,.82)', display: 'flex', flexWrap: 'wrap', gap: '2px 14px' }}>
                                  {dd.partita_iva && <span><span style={{ color: 'rgba(255,255,255,.5)' }}>P.IVA</span> {dd.partita_iva}</span>}
                                  {dd.telefono && <span><span style={{ color: 'rgba(255,255,255,.5)' }}>Tel</span> {dd.telefono}</span>}
                                  {dd.indirizzo && <span><span style={{ color: 'rgba(255,255,255,.5)' }}>Ind.</span> {dd.indirizzo}</span>}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )
            ) : bgEntry && bgEntry.status !== 'running' ? (
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,.8)', padding: '10px 0' }}>Ultima indagine: <b style={{ color: '#fff' }}>{bgEntry.summary || '—'}</b> <span style={{ color: 'rgba(255,255,255,.45)' }}>(fai una nuova ricerca sopra)</span></div>
            ) : (
              <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 13, padding: '10px 0' }}>
                Inserisci un numero fisso e premi Indaga: cerca l'azienda su Google Maps e recupera ragione sociale, P.IVA, indirizzo, categoria, email, <b style={{ color: 'rgba(255,255,255,.75)' }}>sito e social</b>. Se c'è un sito puoi lanciare l'indagine dominio (456 + Copertura + Lucchetto). Puoi chiudere: l'indagine continua in background.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Seconda finestra fumé: confronto/aggiornamento del record esistente */}
      <IndagaConfrontaDialog
        open={confrontaOpen}
        onClose={() => setConfrontaOpen(false)}
        numeroId={data?.numero_id}
        proposte={data?.proposte}
        conferma={data?.conferma}
        dati={data}
      />
    </>,
    document.body
  );
}
